import request from './request'
import { isWechatBrowser, getWechatEnvironment } from '@/utils/wechatEnvironment'

/**
 * 改进的微信登录API
 * 支持多平台微信登录：扫码登录、H5授权登录等
 */

/**
 * 微信扫码登录 - 开放平台网站应用
 * @param {string} code 微信授权码
 * @param {string} state 状态参数
 * @returns {Promise}
 */
export function wechatQrcodeLogin(code, state) {
  return request({
    url: '/api/auth/wechat/qrcode',
    method: 'post',
    data: {
      code,
      state,
      platform: 'openPlatform'
    }
  })
}

/**
 * 微信H5授权登录 - 公众号网页授权
 * @param {string} code 微信授权码
 * @param {string} state 状态参数
 * @returns {Promise}
 */
export function wechatH5Login(code, state) {
  return request({
    url: '/api/auth/wechat/h5',
    method: 'post',
    data: {
      code,
      state,
      platform: 'officialAccount'
    }
  })
}



/**
 * 生成微信登录二维码
 * @param {string} scene 场景值
 * @param {string} platform 平台类型
 * @returns {Promise}
 */
export function generateWechatQrcode(scene, platform = 'openPlatform') {
  return request({
    url: '/api/auth/wechat/qrcode/generate',
    method: 'post',
    data: {
      scene,
      platform
    }
  })
}

/**
 * 获取微信登录二维码参数
 * 前端使用这些参数自行生成二维码
 * @param {string} scene 场景值
 * @param {string} platform 平台类型
 * @returns {Promise}
 */
export function getWechatQrcodeParams(scene, platform = 'openPlatform') {
  return request({
    url: '/api/wechat/qrcode-params',
    method: 'post',
    data: {
      scene,
      platform
    }
  })
}

/**
 * 检查微信登录状态
 * @param {string} scene 场景值
 * @returns {Promise}
 */
export function checkWechatLoginStatus(scene) {
  return request({
    url: '/api/wechat/login-status',
    method: 'get',
    params: {
      scene
    }
  })
}

/**
 * 获取微信用户信息
 * @param {string} token 用户token
 * @returns {Promise}
 */
export function getWechatUserInfo(token) {
  return request({
    url: '/api/user/wechat/info',
    method: 'get',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
}

/**
 * 绑定微信账号
 * @param {string} code 微信授权码
 * @param {string} platform 平台类型
 * @returns {Promise}
 */
export function bindWechatAccount(code, platform) {
  return request({
    url: '/api/user/wechat/bind',
    method: 'post',
    data: {
      code,
      platform
    },
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  })
}

/**
 * 解绑微信账号
 * @param {string} platform 平台类型
 * @returns {Promise}
 */
export function unbindWechatAccount(platform) {
  return request({
    url: '/api/user/wechat/unbind',
    method: 'post',
    data: {
      platform
    },
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  })
}

/**
 * 获取微信JS-SDK配置
 * @param {string} url 当前页面URL
 * @returns {Promise}
 */
export function getWechatJsConfig(url, shareData = null) {
  // 标准化URL处理函数，与wechatShareEnhanced.js保持一致
  const normalizeUrlForWechat = (inputUrl) => {
    try {
      // 使用传入的URL或当前页面URL
      const targetUrl = inputUrl || window.location.href;
      
      // 创建URL对象进行标准化处理
      const urlObj = new URL(targetUrl);
      
      // 构建标准化的URL：协议 + 主机 + 路径 + 查询参数（不包含hash）
      const normalizedUrl = `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}${urlObj.search}`;
      
      console.log('URL标准化处理:', {
        原始URL: targetUrl,
        标准化URL: normalizedUrl,
        协议: urlObj.protocol,
        主机: urlObj.host,
        路径: urlObj.pathname,
        查询参数: urlObj.search,
        hash: urlObj.hash
      });
      
      return normalizedUrl;
    } catch (error) {
      console.error('URL标准化处理失败:', error);
      // 降级处理：移除hash但保留查询参数
      const fallbackUrl = (inputUrl || window.location.href).split('#')[0];
      console.log('使用降级URL处理:', fallbackUrl);
      return fallbackUrl;
    }
  };
  
  const processedUrl = normalizeUrlForWechat(url);
  
  // 准备请求参数
  const requestParams = {
    url: processedUrl
  };
  
  // 如果提供了分享数据，添加到请求参数中
  if (shareData) {
    requestParams.title = shareData.title;
    requestParams.desc = shareData.desc || shareData.description;
    requestParams.link = shareData.link;
    requestParams.imgUrl = shareData.imgUrl;
    
    console.log('包含分享参数的请求:', {
      title: shareData.title,
      desc: shareData.desc || shareData.description,
      link: shareData.link,
      imgUrl: shareData.imgUrl
    });
  }
  
  // 详细日志记录
  console.log('=== 微信JS-SDK配置请求开始 ===');
  console.log('原始URL参数:', url);
  console.log('当前页面URL:', window.location.href);
  console.log('处理后的URL:', processedUrl);
  console.log('分享数据:', shareData);
  console.log('请求参数:', requestParams);
  console.log('请求时间:', new Date().toISOString());
  
  const startTime = Date.now();
  
  return request({
    url: '/api/wechat/js-config',
    method: 'get',
    params: requestParams
  }).then(response => {
    const endTime = Date.now();
    const duration = endTime - startTime;
    
    console.log('=== 微信JS-SDK配置请求成功 ===');
    console.log('请求耗时:', duration + 'ms');
    console.log('响应时间:', new Date().toISOString());
    console.log('服务端响应数据:', response);
    
    if (response && response.data) {
      console.log('JS-SDK配置详情:');
      console.log('- appId:', response.data.appId);
      console.log('- timestamp:', response.data.timestamp);
      console.log('- nonceStr:', response.data.nonceStr);
      console.log('- signature:', response.data.signature);
      console.log('- jsApiList:', response.data.jsApiList);
      
      // 如果服务端返回了分享参数，记录日志
      if (response.data.shareData) {
        console.log('服务端返回的分享参数:');
        console.log('- title:', response.data.shareData.title);
        console.log('- desc:', response.data.shareData.desc);
        console.log('- link:', response.data.shareData.link);
        console.log('- imgUrl:', response.data.shareData.imgUrl);
      }
      
      // 验证关键字段
      if (!response.data.signature) {
        console.warn('⚠️ 警告: 服务端返回的signature为空');
      }
      if (!response.data.appId) {
        console.warn('⚠️ 警告: 服务端返回的appId为空');
      }
    }
    
    console.log('=== 微信JS-SDK配置请求结束 ===');
    return response;
  }).catch(error => {
    const endTime = Date.now();
    const duration = endTime - startTime;
    
    console.error('=== 微信JS-SDK配置请求失败 ===');
    console.error('请求耗时:', duration + 'ms');
    console.error('错误时间:', new Date().toISOString());
    console.error('错误详情:', error);
    
    if (error.response) {
      console.error('服务端错误响应:');
      console.error('- 状态码:', error.response.status);
      console.error('- 响应数据:', error.response.data);
      console.error('- 响应头:', error.response.headers);
    } else if (error.request) {
      console.error('网络请求错误:', error.request);
    } else {
      console.error('请求配置错误:', error.message);
    }
    
    console.log('=== 微信JS-SDK配置请求结束 ===');
    throw error;
  });
}

/**
 * 刷新微信token
 * @param {string} refreshToken 刷新token
 * @returns {Promise}
 */
export function refreshWechatToken(refreshToken) {
  return request({
    url: '/api/auth/wechat/refresh',
    method: 'post',
    data: {
      refresh_token: refreshToken
    }
  })
}

/**
 * 检查token有效性
 * @param {string} token 用户token
 * @returns {Promise}
 */
export function checkWechatToken(token) {
  return request({
    url: '/api/auth/wechat/check',
    method: 'post',
    data: {
      token
    }
  })
}

/**
 * 微信支付相关接口
 */

/**
 * 创建微信支付订单
 * @param {Object} orderData 订单数据
 * @returns {Promise}
 */
export function createWechatPayOrder(orderData) {
  return request({
    url: '/api/payment/create/',
    method: 'post',
    data: orderData,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  })
}

/**
 * 创建微信Native支付订单（扫码支付）
 * @param {string} orderId 订单ID
 * @returns {Promise}
 */
export function createWechatNativePayOrder(orderId) {
  return request({
    url: '/api/payment/createNative/',
    method: 'post',
    data: {
      order_id: orderId
    },
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  })
}

/**
 * 查询微信支付订单状态
 * @param {string} orderId 订单ID
 * @returns {Promise}
 */
export function queryWechatPayOrder(orderId) {
  return request({
    url: '/api/payment/status',
    method: 'post',
    data: {
      order_id: orderId
    }
  })
}

/**
 * 微信分享相关接口
 */

/**
 * 获取微信分享配置
 * @param {Object} shareData 分享数据
 * @returns {Promise}
 */
export function getWechatShareConfig(shareData) {
  return request({
    url: '/api/wechat/share/config',
    method: 'post',
    data: shareData
  }).then(response => {
    // 添加额外的日志来帮助调试
    console.log('微信分享配置API响应:', response)
    
    // 特殊处理API返回的"成功"消息
    if (response.msg && (response.msg.includes('成功') || response.msg.toLowerCase().includes('success'))) {
      // 如果消息是"成功"但code不是0或200，修正为标准成功格式
      if (response.code !== 0 && response.code !== 200) {
        console.warn('API返回"成功"消息但code不为0或200，修正为标准格式');
        return {
          code: 0,
          msg: response.msg,
          data: response.data || shareData
        };
      }
    }
    
    return response;
  }).catch(error => {
    console.error('微信分享配置API调用失败:', error)
    
    // 特殊处理包含"成功"的错误
    if (error && error.message && (
      error.message.includes('成功') || 
      error.message.toLowerCase().includes('success')
    )) {
      console.warn('API错误中包含"成功"，返回模拟成功响应');
      return {
        code: 0,
        msg: '成功',
        data: shareData
      };
    }
    
    // 重新抛出错误以便上层处理
    throw error;
  })
}

/**
 * 工具函数
 */

/**
 * 检测是否在微信浏览器中
 * @returns {boolean}
 */
// 微信环境检测函数已移至 @/utils/wechatEnvironment 统一管理
// 这里重新导出以保持API兼容性
export { isWechatBrowser, getWechatEnvironment } from '@/utils/wechatEnvironment'

/**
 * 生成随机字符串
 * @param {number} length 长度
 * @returns {string}
 */
export function generateRandomString(length = 16) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * 微信登录配置验证
 * @param {Object} config 配置对象
 * @returns {Object} 验证结果
 */
export function validateWechatConfig(config) {
  const errors = []
  
  // 验证开放平台配置
  if (config.openPlatform) {
    if (!config.openPlatform.appId) {
      errors.push('缺少微信开放平台AppID')
    } else if (!config.openPlatform.appId.startsWith('wx')) {
      errors.push('微信开放平台AppID格式错误，应以wx开头')
    }
  }
  
  // 验证公众号配置
  if (config.officialAccount) {
    if (!config.officialAccount.appId) {
      errors.push('缺少微信公众号AppID')
    }
  }
  

  
  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * 默认导出
 */
export default {
  // 登录相关
  wechatQrcodeLogin,
  wechatH5Login,
  
  // 二维码相关
  generateWechatQrcode,
  getWechatQrcodeParams,
  checkWechatLoginStatus,
  
  // 用户信息
  getWechatUserInfo,
  
  // 账号绑定
  bindWechatAccount,
  unbindWechatAccount,
  
  // JS-SDK
  getWechatJsConfig,
  
  // Token管理
  refreshWechatToken,
  checkWechatToken,
  
  // 支付相关
  createWechatPayOrder,
  createWechatNativePayOrder,
  queryWechatPayOrder,
  
  // 分享相关
  getWechatShareConfig,
  
  // 工具函数
  isWechatBrowser,
  getWechatEnvironment,
  generateRandomString,
  validateWechatConfig
}


/**
 * 获取微信支付openid
 * @returns {Promise}
 */
export function getWechatPayOpenid() {
  return request({
    url: '/api/payment/openid',
    method: 'get',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  })
}